import React, { useEffect, useRef } from "react";
import { useForm, Path } from "react-hook-form";
import type { FieldErrors, UseFormRegister } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { callApi, HttpMethod } from "src/services/apiService";
import { legalEntityFullSchema } from "../utils/validationSchemas";
import { TC } from "../utils/trustCallApi";
import { formatPhoneNumber } from "../utils/formatPhone";
import type { LegalEntity, LegalEntitySelectItem } from "../types";
import { NORTH_AMERICAN_REGIONS } from "src/data/us-states-and-can-provinces";
import { AGENT_READ_ONLY_MESSAGE } from "../hooks/useAccountConfig";

// countryCode and isDefault have .default() and callsPerMonth has a preprocess step, so what the
// inputs hold differs from what validation produces. @hookform/resolvers v5 types those two sides
// separately, so useForm has to be told both: input for register/defaultValues, output for submit.
type LegalEntityFormData = z.output<typeof legalEntityFullSchema>;
type LegalEntityFormInput = z.input<typeof legalEntityFullSchema>;

interface IndustryOption {
  text: string;
  value: string;
}

interface LegalEntityFormProps {
  initialData?: LegalEntity;
  onSave: (data: Partial<LegalEntity>) => void;
  isSaving: boolean;
  legalEntities?: LegalEntitySelectItem[];
  onSelectEntity?: (legalName: string) => void;
  onRenameClick?: (legalName: string) => void;
  onDefaultChange?: (isDefault: boolean) => void;
  /**
   * DNC-390: true for the Agent role. Greys out every field and Save, but not the
   * Legal Company Name selector — switching which entity you are looking at is a
   * read. The parent withholds onRenameClick for Agents, which drops that link.
   */
  disabled: boolean;
}

// Hiya's enrollment API requires 2-letter postal codes for state/province, so the
// select must store codes ("TX") while displaying full names ("Texas"). A previous
// version of this form stored full names, which caused Hiya to reject every
// enrollment for entities created here (July 2026 enroll-400 incident).


// Format raw EIN digits to XX-XXXXXXX
function formatEinValue(val: string | undefined | null): string {
  if (!val) return "";
  const cleaned = val.replace(/\D/g, "").substring(0, 9);
  const match = cleaned.match(/^(\d{2})(\d{1,7})?$/);
  if (match) return match[2] ? `${match[1]}-${match[2]}` : match[1];
  return cleaned;
}

// Marks a field a provider will reject the submission without, so someone opening a legacy entity
// can tell a required gap from a merely empty optional field.
const RequiredMark = () => (
  <span className="text-danger ms-1" title="Required for carrier submission">
    *
  </span>
);

// Declared at module scope, not inside LegalEntityForm. A component defined in the render body gets
// a fresh identity every render, so React unmounts and remounts its subtree -- which for the input
// below meant losing the caret the moment a field flipped from invalid to valid, i.e. on the first
// character typed into any required field.
const Field: React.FC<{
  label: string;
  name: keyof LegalEntityFormInput;
  register: UseFormRegister<LegalEntityFormInput>;
  errors: FieldErrors<LegalEntityFormInput>;
  type?: string;
  placeholder?: string;
  readOnly?: boolean;
  value?: string | number;
  required?: boolean;
  disabled?: boolean;
}> = ({
  label,
  name,
  register,
  errors,
  type = "text",
  placeholder,
  readOnly,
  value,
  required,
  disabled,
}) => (
  <div style={{ marginBottom: 4, minHeight: 74 }}>
    <label className="form-label mb-1">
      {label}
      {required && <RequiredMark />}
    </label>
    {readOnly ? (
      <input type="text" className="form-control tc-readonly-input" readOnly value={value} />
    ) : (
      <input
        type={type}
        className={`form-control ${errors[name] ? "is-invalid" : ""}`}
        placeholder={placeholder}
        disabled={disabled}
        {...register(name as Path<LegalEntityFormInput>)}
      />
    )}
    {!readOnly && errors[name] && (
      <div className="invalid-feedback d-block">{errors[name]?.message as string}</div>
    )}
  </div>
);

const LegalEntityForm: React.FC<LegalEntityFormProps> = ({
  initialData,
  onSave,
  isSaving,
  legalEntities,
  onSelectEntity,
  onRenameClick,
  onDefaultChange,
  disabled,
}) => {
  // Fetch industries from API — matches MVC GetLegalEntity which returns { entity, industries, ... }
  const industriesQuery = useQuery<IndustryOption[]>({
    queryKey: ["trustcall", "settings", "legal-entities", "industries"],
    queryFn: () => callApi(TC.SETTINGS_LEGAL_ENTITIES_INDUSTRIES, HttpMethod.GET),
    staleTime: 10 * 60 * 1000, // 10 min — industry list rarely changes
  });
  const industries = industriesQuery.data ?? [];

  // Only use industryId if it matches an available option; otherwise default to ""
  // so the select shows "Select..." and validation correctly blocks Save.
  const resolvedIndustryId =
    initialData?.industryId && industries.some((ind) => ind.value === initialData.industryId)
      ? initialData.industryId
      : "";

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    trigger,
    formState: { errors, touchedFields, dirtyFields },
  } = useForm<LegalEntityFormInput, unknown, LegalEntityFormData>({
    resolver: zodResolver(legalEntityFullSchema),
    mode: "onChange",
    defaultValues: {
      legalName: initialData?.legalName ?? "",
      ein: formatEinValue(initialData?.ein),
      streetAddress: initialData?.streetAddress ?? "",
      city: initialData?.city ?? "",
      state: initialData?.state ?? "",
      zip: initialData?.zip ?? "",
      countryCode: initialData?.countryCode ?? "US",
      industryId: resolvedIndustryId,
      primaryContactName: initialData?.primaryContactName ?? "",
      primaryContactEmail: initialData?.primaryContactEmail ?? "",
      primaryContactPhoneNumber: formatPhoneNumber(initialData?.primaryContactPhoneNumber),
      url: initialData?.url ?? "",
      dunsNumber: initialData?.dunsNumber ?? "",
      primaryPhoneNumber: formatPhoneNumber(initialData?.primaryPhoneNumber),
      callsPerMonth: initialData?.callsPerMonth ?? undefined,
      employeeCount: initialData?.employeeCount ?? undefined,
      dba: initialData?.dba ?? "",
      licenceNumber: initialData?.licenceNumber ?? "",
      callPurposeDescription: initialData?.callPurposeDescription ?? "",
      potentialNegativeReaction: initialData?.potentialNegativeReaction ?? "",
      tnsEnterpriseId: initialData?.tnsEnterpriseId ?? "",
      isDefault: initialData?.isDefault ?? false,
    },
  });

  // Reset form when initialData changes (user selects different entity)
  useEffect(() => {
    reset({
      legalName: initialData?.legalName ?? "",
      ein: formatEinValue(initialData?.ein),
      streetAddress: initialData?.streetAddress ?? "",
      city: initialData?.city ?? "",
      state: initialData?.state ?? "",
      zip: initialData?.zip ?? "",
      countryCode: initialData?.countryCode ?? "US",
      industryId: resolvedIndustryId,
      primaryContactName: initialData?.primaryContactName ?? "",
      primaryContactEmail: initialData?.primaryContactEmail ?? "",
      primaryContactPhoneNumber: formatPhoneNumber(initialData?.primaryContactPhoneNumber),
      url: initialData?.url ?? "",
      dunsNumber: initialData?.dunsNumber ?? "",
      primaryPhoneNumber: formatPhoneNumber(initialData?.primaryPhoneNumber),
      callsPerMonth: initialData?.callsPerMonth ?? undefined,
      employeeCount: initialData?.employeeCount ?? undefined,
      dba: initialData?.dba ?? "",
      licenceNumber: initialData?.licenceNumber ?? "",
      callPurposeDescription: initialData?.callPurposeDescription ?? "",
      potentialNegativeReaction: initialData?.potentialNegativeReaction ?? "",
      tnsEnterpriseId: initialData?.tnsEnterpriseId ?? "",
      isDefault: initialData?.isDefault ?? false,
    });
    void trigger();
  }, [initialData, reset, trigger]);

  // Sync industryId once when industries first load (without resetting user edits on other fields).
  // A ref prevents re-firing after the user has already picked a different industry.
  const industrySyncedRef = useRef(false);
  useEffect(() => {
    // Reset the flag when the entity changes so the next load syncs correctly
    industrySyncedRef.current = false;
  }, [initialData]);
  useEffect(() => {
    if (!industrySyncedRef.current && industries.length > 0) {
      industrySyncedRef.current = true;
      setValue("industryId", resolvedIndustryId, { shouldValidate: true });
    }
  }, [resolvedIndustryId, industries.length, setValue]);

  const onSubmit = (data: LegalEntityFormData) => {
    onSave({ ...data, legalEntityId: initialData?.legalEntityId } as Partial<LegalEntity>);
  };

  // The failing-rule list the summary below renders and the Save button gates on. Derived from
  // errors rather than formState.isValid, which is false until the first trigger() resolves --
  // that left Save disabled on mount with nothing shown to explain why.
  const problems = Object.entries(errors)
    .filter(([, err]) => err?.message)
    .map(([name, err]) => ({ name, message: err!.message as string }));

  // What the summary lists.
  //
  // Add New (LegalEntityList omits initialData only for that) starts empty on purpose and already
  // shows an inline error on every required field, so the summary lists all of them immediately --
  // it is the checklist for filling the form in.
  //
  // A saved entity is different: mount-time trigger() flags every required field it happens to be
  // missing, and opening an existing record under that list is what we were asked to stop. There
  // the summary is restricted to fields the user has interacted with. Both signals are needed --
  // touchedFields is only set on blur, so on its own the field being edited right now never counts
  // and the summary lags a caret move behind; dirtyFields updates on change and covers it, while
  // touchedFields covers a field the user visited and left empty without changing it.
  const visibleProblems = initialData
    ? problems.filter(
        ({ name }) =>
          touchedFields[name as keyof typeof touchedFields] ||
          dirtyFields[name as keyof typeof dirtyFields],
      )
    : problems;

  // Phone formatting: auto-format as (XXX) XXX-XXXX on keydown (matches MVC)
  const formatPhone = (field: "primaryPhoneNumber" | "primaryContactPhoneNumber") => (e: React.ChangeEvent<HTMLInputElement>) => {
    let cleaned = e.target.value.replace(/\D/g, "");
    if (cleaned.length > 10) cleaned = cleaned.substring(0, 10);
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    const formatted = match ? `(${match[1]}) ${match[2]}-${match[3]}` : cleaned;
    setValue(field, formatted, { shouldValidate: true, shouldDirty: true });
  };

  // EIN formatting: auto-format as XX-XXXXXXX on keydown (matches MVC)
  const formatEin = (e: React.ChangeEvent<HTMLInputElement>) => {
    let cleaned = e.target.value.replace(/\D/g, "");
    if (cleaned.length > 9) cleaned = cleaned.substring(0, 9);
    const match = cleaned.match(/^(\d{2})(\d{1,7})?$/);
    let formatted = cleaned;
    if (match) {
      formatted = match[2] ? `${match[1]}-${match[2]}` : match[1];
    }
    setValue("ein", formatted, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Make Default toggle (left) + Legal Entity ID (right) — same row */}
      {initialData && (
        <div className="row mb-3">
          <div className="col-md-6 d-flex align-items-center">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="makeDefault"
                checked={initialData.isDefault}
                disabled={disabled}
                onChange={(e) => onDefaultChange?.(e.target.checked)}
              />
              <label className="form-check-label fw-bold" htmlFor="makeDefault">
                Make Default Legal Entity
              </label>
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-label mb-1">Legal Entity ID</label>
            <input type="text" className="form-control tc-readonly-input" readOnly value={initialData.legalEntityId} />
          </div>
        </div>
      )}

      <div>
          {/* Row: Legal Company Name | Calls per Month */}
          <div className="row">
            <div className="col-md-6" style={{ minHeight: 74 }}>
              <label className="form-label mb-1">
                Legal Company Name
                <RequiredMark />{" "}
                {onRenameClick && initialData && (
                  <a
                    href="#"
                    className="text-primary text-decoration-underline"
                    onClick={(e) => {
                      e.preventDefault();
                      onRenameClick(initialData.legalName);
                    }}
                  >
                    Rename Legal Entity
                  </a>
                )}
              </label>
              {legalEntities && onSelectEntity ? (
                <select
                  className="form-select"                  value={initialData?.legalName ?? ""}
                  onChange={(e) => onSelectEntity(e.target.value)}
                >
                  {legalEntities.map((le) => (
                    <option key={le.legalEntityId} value={le.legalName}>
                      {le.legalName}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className={`form-control ${errors.legalName ? "is-invalid" : ""}`}                  disabled={disabled}
                  {...register("legalName")}
                />
              )}
              {errors.legalName && (
                <div className="invalid-feedback d-block">{errors.legalName.message}</div>
              )}
            </div>
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="Calls per Month" name="callsPerMonth" type="number" /></div>
          </div>

          {/* Row: EIN | Primary Phone Number */}
          <div className="row">
            <div className="col-md-6" style={{ minHeight: 74 }}>
              <label className="form-label mb-1">EIN</label>
              <input
                type="text"
                className={`form-control ${errors.ein ? "is-invalid" : ""}`}
                placeholder="XX-XXXXXXX"                disabled={disabled}
                {...register("ein")}
                onChange={formatEin}
              />
              {errors.ein && <div className="invalid-feedback d-block">{errors.ein.message}</div>}
            </div>
            <div className="col-md-6" style={{ minHeight: 74 }}>
              <label className="form-label mb-1">
                Primary Phone Number
                <RequiredMark />
              </label>
              <input
                type="text"
                className={`form-control ${errors.primaryPhoneNumber ? "is-invalid" : ""}`}
                placeholder="(XXX) XXX-XXXX"                disabled={disabled}
                {...register("primaryPhoneNumber")}
                onChange={formatPhone("primaryPhoneNumber")}
              />
              {errors.primaryPhoneNumber && <div className="invalid-feedback d-block">{errors.primaryPhoneNumber.message}</div>}
            </div>
          </div>

          {/* Row: Address | City */}
          <div className="row">
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="Address" name="streetAddress" required /></div>
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="City" name="city" required /></div>
          </div>

          {/* Row: State/Province | Postal Code */}
          <div className="row">
            <div className="col-md-6" style={{ minHeight: 74 }}>
              <label className="form-label mb-1">
                State/Province
                <RequiredMark />
              </label>
              <select
                className={`form-select ${errors.state ? "is-invalid" : ""}`}                disabled={disabled}
                {...register("state")}
              >
                <option value="">Select...</option>
                {NORTH_AMERICAN_REGIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {errors.state && <div className="invalid-feedback d-block">{errors.state.message}</div>}
            </div>
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="Postal Code" name="zip" placeholder="12345" required /></div>
          </div>

          {/* Row: Country Code | Employee Count */}
          <div className="row">
            {/* A select, not free text: First Orion wants a 2-letter ISO code, and typed values
                arrived as "USA" often enough to matter. Scoped to the two countries the
                State/Province list above covers. */}
            <div className="col-md-6" style={{ minHeight: 74 }}>
              <label className="form-label mb-1">
                Country
                <RequiredMark />
              </label>
              <select
                className={`form-select ${errors.countryCode ? "is-invalid" : ""}`}
                disabled={disabled}
                {...register("countryCode")}
              >
                <option value="">Select...</option>
                <option value="US">United States (US)</option>
                <option value="CA">Canada (CA)</option>
              </select>
              {errors.countryCode && (
                <div className="invalid-feedback d-block">{errors.countryCode.message}</div>
              )}
            </div>
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="Employee Count" name="employeeCount" type="number" required /></div>
          </div>

          {/* Row: Industry | Contact Name */}
          <div className="row">
            <div className="col-md-6" style={{ minHeight: 74 }}>
              <label className="form-label mb-1">
                Industry
                <RequiredMark />
              </label>
              <select
                className={`form-select ${errors.industryId ? "is-invalid" : ""}`}                disabled={disabled}
                {...register("industryId")}
              >
                <option value="">Select...</option>
                {industries.map((ind) => (
                  <option key={ind.value} value={ind.value}>{ind.text}</option>
                ))}
              </select>
              {errors.industryId && <div className="invalid-feedback d-block">{errors.industryId.message}</div>}
            </div>
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="Contact Name" name="primaryContactName" required /></div>
          </div>

          {/* Row: Contact Email | Contact Phone Number */}
          <div className="row">
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="Contact Email" name="primaryContactEmail" type="email" required /></div>
            <div className="col-md-6" style={{ minHeight: 74 }}>
              <label className="form-label mb-1">
                Contact Phone Number
                <RequiredMark />
              </label>
              <input
                type="text"
                className={`form-control ${errors.primaryContactPhoneNumber ? "is-invalid" : ""}`}
                placeholder="(XXX) XXX-XXXX"                disabled={disabled}
                {...register("primaryContactPhoneNumber")}
                onChange={formatPhone("primaryContactPhoneNumber")}
              />
              {errors.primaryContactPhoneNumber && <div className="invalid-feedback d-block">{errors.primaryContactPhoneNumber.message}</div>}
            </div>
          </div>

          {/* Row: Web URL | DBA */}
          <div className="row">
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="Web URL" name="url" placeholder="example.com" required /></div>
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="DBA" name="dba" /></div>
          </div>

          {/* Row: DUN Number | Real Estate License */}
          <div className="row">
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="DUN Number" name="dunsNumber" /></div>
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="Real Estate / Insurance Licenses Number" name="licenceNumber" /></div>
          </div>

          {/* Row: TNS Enterprise ID | Hiya Business ID */}
          <div className="row">
            <div className="col-md-6"><Field register={register} errors={errors} disabled={disabled} label="TNS Enterprise ID" name="tnsEnterpriseId" /></div>
            <div className="col-md-6">
              <div style={{ marginBottom: 4, minHeight: 74 }}>
                <label className="form-label mb-1" htmlFor="hiyaBusinessId">Hiya Business ID</label>
                <input id="hiyaBusinessId" type="text" className="form-control tc-readonly-input" readOnly value={initialData?.hiyaBusinessId ?? ""} />
              </div>
            </div>
          </div>
      </div>

      {/* Textareas — separate row so they align side-by-side */}
      <div className="row">
        <div className="col-md-6">
          <div className="mb-3">
            <label className="form-label mb-1">
              What is the purpose of the call and why do the recipients want/need these calls?
            </label>
            <textarea
              className={`form-control ${errors.callPurposeDescription ? "is-invalid" : ""}`}
              rows={4}              disabled={disabled}
              {...register("callPurposeDescription")}
            />
            {errors.callPurposeDescription && (
              <div className="invalid-feedback">{errors.callPurposeDescription.message}</div>
            )}
            <div className="form-text">Hiya tickets include only the first 200 characters.</div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="mb-3">
            <label className="form-label mb-1">
              Why might recipients react poorly to these calls, despite being wanted?
            </label>
            <textarea
              className={`form-control ${errors.potentialNegativeReaction ? "is-invalid" : ""}`}
              rows={4}              disabled={disabled}
              {...register("potentialNegativeReaction")}
            />
            {errors.potentialNegativeReaction && (
              <div className="invalid-feedback">{errors.potentialNegativeReaction.message}</div>
            )}
            <div className="form-text">Hiya tickets include only the first 200 characters.</div>
          </div>
        </div>
      </div>

      {/* Every failing rule, listed together. The form is tall enough that an inline error can sit
          well off-screen -- a legacy entity with a bad phone number at the top gives no visible
          reason for a disabled Save button down here. Messages are written to name their own field
          so this needs no field-label lookup.

          On a saved entity it stays out of the way until the user touches something, so a record
          does not open under a list of complaints; after that it appears on the first failing
          field. A new entity shows the full list right away -- see visibleProblems above.

          The live region wrapper renders unconditionally and only its contents toggle: a region
          inserted at the same moment as its text is often not announced. polite rather than
          role="alert" because validation runs on every keystroke. */}
      <div aria-live="polite" aria-atomic="true">
        {visibleProblems.length > 0 && (
          <div className="alert alert-danger py-2 mb-2">
            <div className="fw-bold mb-1">
              Fix{" "}
              {visibleProblems.length === 1 ? "this" : `these ${visibleProblems.length}`} before
              saving:
            </div>
            <ul className="mb-0 ps-3">
              {visibleProblems.map(({ name, message }) => (
                <li key={name}>{message}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Full-width dark Save button */}
      <button
        type="submit"
        className="btn w-100"
        style={{ backgroundColor: "var(--navbar-and-treeview-bg)", borderColor: "var(--navbar-and-treeview-bg)", color: "#fff" }}
        disabled={problems.length > 0 || isSaving || disabled}
        title={
          disabled
            ? AGENT_READ_ONLY_MESSAGE
            : problems.length > 0
              ? "Some fields need fixing before this can be saved"
              : undefined
        }
      >
        <svg viewBox="0 0 512 512" width="14" height="14" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}>
          <path d="M384 32H64c-17.6 0-32 14.4-32 32v384c0 17.6 14.4 32 32 32h384c17.6 0 32-14.4 32-32V128zm-64 32v128h-64V64zm128 384-383.9.1-.1-.1V64.1l.1-.1H96v160h256V64h18.7l77.3 77.3z" />
        </svg>
        <span style={{ verticalAlign: "middle" }}>{isSaving ? "Saving..." : "Save"}</span>
      </button>
    </form>
  );
};

export default LegalEntityForm;
